import { Autocomplete, Button, capitalize, debounce, IconButton, Stack, TextField, Typography, Box, Tooltip } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { CustomerService } from "../../service/customerService";
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse'
import { parsePhoneNumberFromString } from "libphonenumber-js";
import AddIcon from '@mui/icons-material/Add';
import AddNewCustomerDialog from "../customerrecord/dialog/addNewCustomerDialog";
import useDialogModal from "../../hooks/useDialogModal";
import { Can } from "../auth/Can"; // Permission gating (assumes 'customer' section exists with write level for add)

export default function CustomerSearch({ onCustomerSelected, reset, sx, disableAdd = false }) {
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [value, setValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [AddCustomerDialog, showAddCustomerDialog] =
        useDialogModal(AddNewCustomerDialog);

    useEffect(() => {
        setOptions([]);
        setValue(null);
    }, [reset]);

    const fetchCustomers = useMemo(() =>
        debounce(async (request, callback) => {
            const customerSearchResult = await CustomerService.search(request);
            callback(customerSearchResult);
        }, 500), []);

    useEffect(() => {
        let active = true;
        if (inputValue === '') {
            setLoading(false);
            setOptions(value ? [value] : []);
            return undefined;
        }

        // Use the improved 'name' parameter that searches across both first and last names
        // This handles any order: "John Smith", "Smith John", "Joh", "Smi", etc.
        const params = {
            name: inputValue.trim()
        };
        setLoading(true);
        fetchCustomers(params, (result) => {
            if (active) {
                setLoading(false);
                const { code, data } = result; // 'message' unused

                let newOptions = [];
                if (code === 0) {

                    if (value) {
                        newOptions = [value];
                    }

                    if (result) {
                        newOptions = [...newOptions, ...data];
                    }
                }
                setOptions(newOptions);
            }
        });

        return () => active = false;
    }, [value, inputValue, fetchCustomers]);


    const handleAutoCompleteChange = (event, newValue, reason) => {

        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);

        if (reason === 'selectOption') {
            onCustomerSelected(newValue);
        }

        if (reason === 'clear') {
            onCustomerSelected(null);
        }
    }

    const handleAddCustomer = (customer) => {

        if( customer ) {
            setValue({
                fullName: customer.fullName,
                email: customer.email,
                mobile: customer.mobile
            });
    
            onCustomerSelected(customer);
        }

    }

    return (
        <Stack display={"flex"} flexDirection="row" sx={{ ...sx }}>
            <Autocomplete
                id="customer-search-box"
                filterOptions={(x) => x}
                size="small"
                selectOnFocus
                clearOnBlur
                fullWidth
                autoComplete
                autoSelect
                includeInputInList
                filterSelectedOptions
                noOptionsText="No customers found"
                options={options}
                value={value}
                onChange={handleAutoCompleteChange}
                autoHighlight={true}
                loading={loading}
                getOptionLabel={(option) => typeof option === 'string' ? option : capitalize(option.fullName)}
                onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                renderInput={(params) => (
                    <TextField {...params} label="Search..." fullWidth />
                )}

                renderOption={(props, option, { inputValue }) => {

                    const matches = match(option.fullName, inputValue, { insideWords: true });
                    const parts = parse(option.fullName, matches);

                    return (
                        <li {...props} key={option.id}>
                            <div>
                                {parts.map((part, index) => (
                                    <Box key={index} component="span" style={{
                                        textTransform: 'capitalize',
                                        fontWeight: part.highlight ? 700 : 400
                                    }}>
                                        {part.text}
                                    </Box>
                                ))}
                                <Typography variant="body2">
                                    {option.email || 'no email'}
                                </Typography>
                                <Typography variant="body2">
                                    {parsePhoneNumberFromString(`+1${option.mobile}`, 'US')?.formatNational() || option.home}
                                </Typography>
                            </div>
                        </li>
                    )
                }}
            />
            {/* Permission gated Add button using <Can> render prop. */}
            <Can section="customers" action='create'>
                {({ allowed, explain }) => {
                    if (disableAdd || !allowed) return null;
                    return (
                        <Tooltip title="Add new customer">
                            <IconButton onClick={() => showAddCustomerDialog()} aria-label="add-customer" size="small">
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                    );
                }}
            </Can>
            <AddCustomerDialog onDone={handleAddCustomer} ignoreBackdropClick={true} />
        </Stack>

    )
}